import { useContext, useRef, useReducer, useEffect, useCallback } from 'react'
import { AIStateStoreContext } from '../../../context/aiGeneration'
import { areNodeStatesEqual, type AINodeDerivedState, type AIStateStore } from './AIStateStore'

/**
 * Hook that subscribes to AI state for a specific node.
 *
 * This is the key optimization hook: it subscribes to the global AI state store,
 * but ONLY triggers a re-render when THIS specific node's derived state changes.
 *
 * Example: If node A's suggestions change, node B won't re-render because
 * node B's derived state (isGenerating, hasSuggestions, etc.) hasn't changed.
 *
 * @param nodeId - The ID of the node to get state for
 * @returns Derived state for the node (isGenerating, isReady, hasSuggestions, suggestionCount)
 */
export function useAINodeState(nodeId: string): AINodeDerivedState {
  const store = useContext(AIStateStoreContext)

  // Track previous state to enable equality comparison
  const prevStateRef = useRef<AINodeDerivedState | null>(null)

  // Force update mechanism - only called when derived state actually changes
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  // Compute current state from store
  const computeState = useCallback((): AINodeDerivedState => {
    if (!store) {
      return {
        isGenerating: false,
        isReady: false,
        hasSuggestions: false,
        suggestionCount: 0,
      }
    }
    return store.getNodeDerivedState(nodeId)
  }, [store, nodeId])

  // Subscribe to store changes
  useEffect(() => {
    if (!store) return undefined

    const unsubscribe = store.subscribe(() => {
      const newState = computeState()

      // Only trigger re-render if derived state actually changed
      // This is the key optimization - changes to other nodes won't cause re-render
      if (!areNodeStatesEqual(prevStateRef.current, newState)) {
        prevStateRef.current = newState
        forceUpdate()
      }
    })

    return unsubscribe
  }, [store, computeState])

  // Initialize state on first render
  prevStateRef.current ??= computeState()

  return prevStateRef.current
}

/**
 * Hook that returns a stable reference to suggestions for a specific node.
 *
 * This hook is separate from useAINodeState because suggestions are a list
 * that changes more frequently and is only needed by components that actually
 * display the suggestions (like ExternalSuggestionsCard in chat), not by all nodes.
 *
 * @param nodeId - The source node ID to get suggestions for
 * @returns Array of suggestions for this node
 */
export function useAISuggestionsForNode(nodeId: string) {
  const store = useContext(AIStateStoreContext)

  // Track previous suggestions to enable equality comparison
  // Use import type to get the return type without null check issues
  const prevSuggestionsRef = useRef<ReturnType<AIStateStore['getSuggestionsForNode']> | null>(null)
  const prevLengthRef = useRef<number>(0)

  // Force update mechanism
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  // Compute current suggestions from store
  const computeSuggestions = useCallback(() => {
    if (!store) return []
    return store.getSuggestionsForNode(nodeId)
  }, [store, nodeId])

  // Subscribe to store changes
  useEffect(() => {
    if (!store) return undefined

    const unsubscribe = store.subscribe(() => {
      const newSuggestions = computeSuggestions()

      // Simple length + first/last item comparison for performance
      // Full deep equality would be expensive for large suggestion lists
      const lengthChanged = newSuggestions.length !== prevLengthRef.current
      const contentChanged =
        lengthChanged ||
        (newSuggestions.length > 0 &&
          prevSuggestionsRef.current &&
          (newSuggestions[0]?.id !== prevSuggestionsRef.current[0]?.id ||
            newSuggestions[newSuggestions.length - 1]?.id !==
              prevSuggestionsRef.current[prevSuggestionsRef.current.length - 1]?.id ||
            newSuggestions.some((s, i) => {
              const prev = prevSuggestionsRef.current?.[i]
              return s.selected !== prev?.selected
            })))

      if (contentChanged || !prevSuggestionsRef.current) {
        prevSuggestionsRef.current = newSuggestions
        prevLengthRef.current = newSuggestions.length
        forceUpdate()
      }
    })

    return unsubscribe
  }, [store, computeSuggestions])

  // Initialize on first render
  if (!prevSuggestionsRef.current) {
    prevSuggestionsRef.current = computeSuggestions()
    prevLengthRef.current = prevSuggestionsRef.current.length
  }

  return prevSuggestionsRef.current
}
